import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

export interface EditDocumentFormData {
  id: string;
  description: string;
  tags: string[];
}

interface EditDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: EditDocumentFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  open,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedDocument, loading } = useAppSelector((state) => state.documents);

  const [formData, setFormData] = useState({
    description: '',
    tags: '',
  });

  useEffect(() => {
    if (selectedDocument) {
      setFormData({
        description: selectedDocument.description || '',
        tags: selectedDocument.tags?.join(', ') || '',
      });
    }
  }, [selectedDocument, open]);

  const handleSubmit = () => {
    if (!selectedDocument) return;

    const tags = formData.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    onSave({
      id: selectedDocument.id,
      description: formData.description,
      tags,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Documento</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedDocument ? (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />

            <TextField
              label="Tags (separados por comas)"
              value={formData.tags}
              onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese las etiquetas separadas por comas"
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDocumentModal;
